import React, { useState, useEffect, useCallback } from 'react';
import { t } from '@lingui/macro';
import moment from 'moment';
import Loader from '../Loader';
import { IMart } from '../../../interfaces/types';
import { fetchFootfallTotal } from '../../../../reducers/api/getReportData';
import {
    getAttendanceZone,
    IReportComparisonRow,
    IReportBreakdownRow,
} from '../../../helpers/reportHelpers';
import { numberWithSpaces } from '../../../helpers/index';
import ReportComparisonChart from './ReportComparisonChart';
import ReportComparisonTable from './ReportComparisonTable';
import ExportButtons from './ExportButtons';

interface IProps {
    mart: IMart;
    referenceDate: moment.Moment;
    yearsBack: number;
}

const ReportDaily = ({ mart, referenceDate, yearsBack }: IProps) => {
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState<IReportComparisonRow[]>([]);
    const [locationRows, setLocationRows] = useState<IReportBreakdownRow[]>([]);
    const [zoneRows, setZoneRows] = useState<IReportBreakdownRow[]>([]);
    const [yearColumns, setYearColumns] = useState<{ year: number; label: string }[]>([]);

    const refDateStr = referenceDate.format('YYYY-MM-DD');

    const fetchData = useCallback(async () => {
        setLoading(true);

        const attendanceZone = getAttendanceZone(mart);
        const zoneIds = attendanceZone ? [attendanceZone.id] : [];

        // Build year columns
        const cols: { year: number; label: string }[] = [];
        for (let y = 0; y <= yearsBack; y++) {
            const d = referenceDate.clone().subtract(y, 'years');
            cols.push({ year: d.year(), label: d.format('DD.MM.YYYY') });
        }
        setYearColumns(cols);

        try {
            // --- Section 1: Total daily footfall ---
            const totalPromises: Promise<number>[] = [];
            for (let y = 0; y <= yearsBack; y++) {
                const d = referenceDate.clone().subtract(y, 'years');
                const dateStr = d.format('YYYY-MM-DD');
                totalPromises.push(fetchFootfallTotal(dateStr, dateStr, zoneIds, []));
            }
            const totalValues = await Promise.all(totalPromises);

            const tRows: IReportComparisonRow[] = totalValues.map((val, y) => {
                const d = referenceDate.clone().subtract(y, 'years');
                return {
                    year: d.year(),
                    label: d.format('DD.MM.YYYY'),
                    dateFrom: d.format('YYYY-MM-DD'),
                    dateTo: d.format('YYYY-MM-DD'),
                    value: val
                };
            });
            setTotalRows(tRows);

            // --- Section 2: By Location ---
            const locRows: IReportBreakdownRow[] = [];
            for (const loc of mart.location) {
                const locPromises: Promise<number>[] = [];
                for (let y = 0; y <= yearsBack; y++) {
                    const d = referenceDate.clone().subtract(y, 'years');
                    const dateStr = d.format('YYYY-MM-DD');
                    locPromises.push(fetchFootfallTotal(dateStr, dateStr, [], [loc.id]));
                }
                const locValues = await Promise.all(locPromises);
                const values = locValues.map((val, y) => ({
                    year: referenceDate.clone().subtract(y, 'years').year(),
                    value: val
                }));
                locRows.push({ label: loc.name, values });
            }
            setLocationRows(locRows);

            // --- Section 3: By Zone ---
            const zRows: IReportBreakdownRow[] = [];
            for (const zone of mart.zone) {
                const zPromises: Promise<number>[] = [];
                for (let y = 0; y <= yearsBack; y++) {
                    const d = referenceDate.clone().subtract(y, 'years');
                    const dateStr = d.format('YYYY-MM-DD');
                    zPromises.push(fetchFootfallTotal(dateStr, dateStr, [zone.id], []));
                }
                const zValues = await Promise.all(zPromises);
                const values = zValues.map((val, y) => ({
                    year: referenceDate.clone().subtract(y, 'years').year(),
                    value: val
                }));
                zRows.push({ label: zone.name, values });
            }
            setZoneRows(zRows);
        } catch (e) {
            console.error('Error fetching daily report data', e);
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mart, refDateStr, yearsBack]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Convert totalRows to breakdown format for the table
    const totalBreakdownRows: IReportBreakdownRow[] = totalRows.length > 0
        ? [{ label: t`Celkom`, values: totalRows.map(r => ({ year: r.year, value: r.value })) }]
        : [];

    const buildXlsxInput = () => ({
        martName:   mart.name,
        reportType: t`Denný report`,
        dateLabel:  referenceDate.format('DD.MM.YYYY'),
        columns:    yearColumns,
        sections: [
            ...(totalBreakdownRows.length > 0
                ? [{ title: t`Celková denná návštevnosť`, rows: totalBreakdownRows, layout: 'total' as const, chartId: 'daily-total' }]
                : []),
            ...(locationRows.length > 0
                ? [{ title: t`Návštevnosť podľa vstupov`, rows: locationRows, layout: 'breakdown' as const }]
                : []),
            ...(zoneRows.length > 0
                ? [{ title: t`Návštevnosť podľa zón`, rows: zoneRows, layout: 'breakdown' as const }]
                : []),
        ],
        kpis: totalRows.length > 0 ? [
            {
                label: t`Celková návštevnosť`,
                value: numberWithSpaces(totalRows[0].value),
                sub:   referenceDate.format('DD.MM.YYYY'),
            },
        ] : undefined,
    });

    return (
        <>
            {/* Section 1: Total daily footfall */}
            <div className="box mgt-30 mgb-30 pd-40 pdl-25lg pdr-25lg" style={{ position: 'relative' }}>
                <div className="loader-wrapper" style={{ position: loading ? 'relative' : undefined, overflow: loading ? 'hidden' : undefined, minHeight: loading ? 120 : undefined }}>
                    <Loader value={loading} />
                </div>
                {!loading && (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                            <ExportButtons buildInput={buildXlsxInput} />
                        </div>
                        <ReportComparisonChart
                            chartId="daily-total"
                            title={t`Celková denná návštevnosť`}
                            subtitle={referenceDate.format('dddd, DD.MM.YYYY')}
                            rows={totalRows}
                        />
                        <ReportComparisonTable
                            title={t`Celková denná návštevnosť`}
                            columns={yearColumns}
                            rows={totalBreakdownRows}
                        />
                    </>
                )}
            </div>

            {/* Section 2: By Location */}
            {!loading && locationRows.length > 0 && (
                <div className="box mgt-30 mgb-30 pd-40 pdl-25lg pdr-25lg">
                    <ReportComparisonTable
                        title={t`Návštevnosť podľa vstupov`}
                        columns={yearColumns}
                        rows={locationRows}
                    />
                </div>
            )}

            {/* Section 3: By Zone */}
            {!loading && zoneRows.length > 0 && (
                <div className="box mgt-30 mgb-30 pd-40 pdl-25lg pdr-25lg">
                    <ReportComparisonTable
                        title={t`Návštevnosť podľa zón`}
                        columns={yearColumns}
                        rows={zoneRows}
                    />
                </div>
            )}
        </>
    );
};

export default ReportDaily;
